import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Table, Select, DatePicker, Modal, Pagination, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { auditApi, tenantApi, userApi } from "../../services/api";
import { useEnvironment } from "../../context/EnvironmentContext";
import useBreakpoint from "../../hooks/useBreakpoint";

const { RangePicker } = DatePicker;

const FILTER_KEYS = ["tenant", "user", "action", "startDate", "endDate", "page", "pageSize"];

function filtersFromParams(searchParams) {
  const f = { page: 1, pageSize: 20 };
  for (const key of FILTER_KEYS) {
    const val = searchParams.get(key);
    if (val != null && val !== "") {
      if (key === "page" || key === "pageSize") {
        f[key] = parseInt(val) || f[key];
      } else {
        f[key] = val;
      }
    }
  }
  return f;
}

function filtersToParams(filters) {
  const params = {};
  for (const key of FILTER_KEYS) {
    const val = filters[key];
    if (val != null && val !== undefined) {
      // Skip default page/pageSize to keep URL clean
      if (key === "page" && val === 1) continue;
      if (key === "pageSize" && val === 20) continue;
      params[key] = String(val);
    }
  }
  return params;
}

export default function AuditLogPage() {
  const { selectedEnv } = useEnvironment();
  const { isMobile } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [detailLog, setDetailLog] = useState(null);

  // Sync filters → URL query params
  const syncParams = useCallback((newFilters) => {
    setSearchParams(filtersToParams(newFilters), { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    tenantApi.getAll().then((res) => setTenants(res.data)).catch(() => {});
  }, [selectedEnv]);

  useEffect(() => {
    userApi.getAll().then((res) => setUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchLogs();
    syncParams(filters);
  }, [selectedEnv, filters]); // eslint-disable-line

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, pageSize: filters.pageSize };
      if (filters.tenant) params.tenant = filters.tenant;
      if (filters.user) params.user = filters.user;
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await auditApi.getLogs(params);
      setLogs(res.data.data);
      setTotal(res.data.total);
    } catch { message.error("Failed to fetch audit logs"); }
    finally { setLoading(false); }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  // Derive RangePicker value from filters
  const dateRangeValue = filters.startDate && filters.endDate
    ? [dayjs(filters.startDate), dayjs(filters.endDate)]
    : undefined;

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "created_at",
      width: 170,
      render: (val) => val ? (
        <span className="text-[12px] text-slate-500">
          {new Date(val).toLocaleString()}
        </span>
      ) : "--",
    },
    {
      title: "User",
      dataIndex: "user_email",
      width: 200,
      ellipsis: true,
      render: (val) => <span className="font-medium">{val}</span>,
    },
    { title: "Tenant", dataIndex: "tenant_name", width: 140 },
    { title: "Entity", dataIndex: "entity", width: 140 },
    { title: "Field", dataIndex: "field_name", width: 120 },
    {
      title: "Old Value",
      dataIndex: "old_value",
      width: 180,
      ellipsis: true,
      render: (val) => val || <span className="text-slate-300">--</span>,
    },
    {
      title: "New Value",
      dataIndex: "new_value",
      width: 180,
      ellipsis: true,
      render: (val) => val || <span className="text-slate-300">--</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 90,
      render: (action) => (
        <span className={`action-tag ${(action || "").toLowerCase()}`}>
          {action}
        </span>
      ),
    },
    {
      title: "Source",
      width: 180,
      render: (_, record) => {
        if (record.action !== "COPY" || (!record.source_env && !record.source_tenant)) {
          return <span className="text-slate-300">--</span>;
        }
        const envVariant = record.source_env === "prod" ? "prod" : record.source_env === "stage" ? "stage" : "dev";
        return (
          <div className="text-[12px] leading-[1.5]">
            {record.source_env && (
              <span className={`env-flag ${envVariant}`}>
                {record.source_env}
              </span>
            )}
            {record.source_tenant && (
              <span className="text-slate-500">{record.source_tenant}</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Audit Logs</h1>
        <p className="page-subtitle">Track configuration changes across tenants</p>
      </div>

      <div className="filter-bar">
        <Select
          placeholder="Tenant"
          allowClear
          value={filters.tenant}
          className="w-[200px]"
          options={tenants.map((t) => ({ label: t.subdomain, value: t.name }))}
          onChange={(val) => updateFilter("tenant", val)}
          showSearch
          suffixIcon={<SearchOutlined className="text-slate-400" />}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        />
        <Select
          placeholder="User"
          allowClear
          value={filters.user}
          className="w-[220px]"
          options={users.map((u) => ({
            label: u.name || u.email,
            value: u.email,
          }))}
          onChange={(val) => updateFilter("user", val)}
          showSearch
          suffixIcon={<SearchOutlined className="text-slate-400" />}
          filterOption={(input, option) =>
            (option.label || "").toLowerCase().includes(input.toLowerCase()) ||
            (option.value || "").toLowerCase().includes(input.toLowerCase())
          }
          optionRender={(option) => (
            <div>
              <div className="font-medium text-[13px]">{option.label}</div>
              <div className="text-[11px] text-slate-400">{option.value}</div>
            </div>
          )}
        />
        <Select
          placeholder="Action"
          allowClear
          value={filters.action}
          className="w-[130px]"
          options={[
            { label: "Create", value: "CREATE" },
            { label: "Update", value: "UPDATE" },
            { label: "Copy", value: "COPY" },
            { label: "Delete", value: "DELETE" },
          ]}
          onChange={(val) => updateFilter("action", val)}
        />
        <RangePicker
          value={dateRangeValue}
          onChange={(dates) => {
            if (dates) {
              const start = dates[0].startOf("day").toISOString();
              const end = dates[1].endOf("day").toISOString();
              updateFilter("startDate", start);
              setFilters((prev) => ({ ...prev, endDate: end, page: 1 }));
            } else {
              updateFilter("startDate", undefined);
              setFilters((prev) => ({ ...prev, endDate: undefined, page: 1 }));
            }
          }}
        />
      </div>

      {!isMobile ? (
        <div className="sq-table">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={logs}
            columns={columns}
            size="middle"
            scroll={{ x: true }}
            pagination={{
              current: filters.page,
              pageSize: filters.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (t) => (
                <span className="text-slate-500 text-[13px]">{t} records</span>
              ),
              size: "small",
              onChange: (page, pageSize) =>
                setFilters((prev) => ({ ...prev, page, pageSize })),
            }}
          />
        </div>
      ) : (
        <>
          <div className="card-list">
            {loading && (
              <div className="empty-state py-6 px-3">
                <div className="empty-state-text">Loading...</div>
              </div>
            )}
            {!loading && logs.length === 0 && (
              <div className="empty-state py-6 px-3">
                <div className="empty-state-text">No records</div>
              </div>
            )}
            {!loading && logs.map((log) => (
              <div
                key={log.id}
                className="card-list-item cursor-pointer"
                onClick={() => setDetailLog(log)}
              >
                <div className="flex justify-between gap-2 items-start">
                  <div className="min-w-0 flex-1">
                    <div className="card-list-item-title text-[13px] truncate">
                      {log.user_email}
                    </div>
                    <div className="card-list-item-sub">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "--"}
                    </div>
                  </div>
                  <span className={`action-tag ${(log.action || "").toLowerCase()}`}>
                    {log.action}
                  </span>
                </div>
                <div className="card-list-item-sub flex flex-wrap gap-1.5">
                  {log.tenant_name && <span>{log.tenant_name}</span>}
                  {log.entity && <span>• {log.entity}</span>}
                  {log.field_name && <span>• {log.field_name}</span>}
                </div>
                {(log.old_value || log.new_value) && (
                  <div className="code-box text-[11px] text-slate-600">
                    {(log.old_value || "—")} → {(log.new_value || "—")}
                  </div>
                )}
              </div>
            ))}
          </div>
          {total > 0 && !loading && (
            <div className="mt-4 flex justify-center">
              <Pagination
                current={filters.page}
                pageSize={filters.pageSize}
                total={total}
                size="small"
                simple
                onChange={(page, pageSize) => setFilters((prev) => ({ ...prev, page, pageSize }))}
              />
            </div>
          )}
        </>
      )}

      {/* Mobile-only detail modal */}
      <Modal
        title="Audit log entry"
        open={!!detailLog}
        onCancel={() => setDetailLog(null)}
        footer={null}
        destroyOnClose
      >
        {detailLog && (
          <div className="flex flex-col gap-2.5 text-[13px]">
            <DetailRow label="Timestamp" value={detailLog.created_at ? new Date(detailLog.created_at).toLocaleString() : "--"} />
            <DetailRow label="User" value={detailLog.user_email} />
            <DetailRow label="Tenant" value={detailLog.tenant_name} />
            <DetailRow label="Action" value={<span className={`action-tag ${(detailLog.action || "").toLowerCase()}`}>{detailLog.action}</span>} />
            <DetailRow label="Entity" value={detailLog.entity} />
            <DetailRow label="Field" value={detailLog.field_name} />
            <DetailRow label="Old value" value={detailLog.old_value} mono />
            <DetailRow label="New value" value={detailLog.new_value} mono />
            {detailLog.action === "COPY" && (detailLog.source_env || detailLog.source_tenant) && (
              <DetailRow label="Source" value={`${detailLog.source_env || ""}${detailLog.source_env && detailLog.source_tenant ? " / " : ""}${detailLog.source_tenant || ""}`} />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="mini-label tracking-[0.4px]">{label}</span>
      <span className={`[word-break:break-word] ${mono ? "font-[monospace] " : ""}${value ? "text-slate-800" : "text-slate-300"}`}>
        {value || "—"}
      </span>
    </div>
  );
}
